import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  Pagination,
  useDisclosure,
} from '@heroui/react';
import { useMemo, useState } from 'react';
import { useFetchAllCertificate } from '../../../hook/useQueryData';
import CustomChip from '../../components/CustomChip';
import moment from 'moment';
import SmallLoadingSpinner from '../../components/common/ui/SmallLoadingSpinner';

const ProviderCertificate = ({ id }: any) => {
  const descriptionModal = useDisclosure();
  const [currentPage, setCurrentPage] = useState<number>(1);

  const { data, isPending, isError, error } = useFetchAllCertificate({
    id: id,
    page: currentPage,
    limit: 10,
  });

  const certificateData = data?.items;
  const totalPage = useMemo(() => data?.pages || 1, [data]);

  return (
    <div className="mt-5">
      {isPending ? (
        <div className="flex justify-center items-center">
          <SmallLoadingSpinner label="Loading..." />
        </div>
      ) : isError ? (
        <p className="text-danger">
          {error instanceof Error
            ? error.message
            : error || 'Something went wrong'}
        </p>
      ) : certificateData?.length === 0 ? (
        <p className="text-sm text-gray-400">No Certificate found.</p>
      ) : (
        <>
          <p className="text-xs text-gray-500 italic mt-4">
            Disclaimer: The certificates and licenses displayed on this page
            have been provided by the business and have not been independently
            verified by HirePanther. Please confirm the validity of any
            credentials directly with the relevant issuing authority before
            engaging their services.
          </p>
          <div className="mt-6 bg-white grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {certificateData?.map((item: any, index: number) => (
              <div
                key={index}
                className="border border-secondary/35 shadow-sm rounded-lg p-4 flex flex-col"
              >
                {/* Card Content */}
                <div className="flex flex-col-reverse sm:flex-row justify-between items-start -mt-2 sm:mt-0">
                  <div className="flex flex-col gap-1">
                    <p className="text-body-bold line-clamp-2">
                      {item?.name || '-'}
                    </p>
                    <p className="text-caption line-clamp-2">
                      Issued by: {item?.issuedBy || '-'}
                    </p>
                  </div>
                  <div className="text-right">
                    <CustomChip
                      label={
                        moment(item?.expiryDate).isBefore()
                          ? 'Expired'
                          : 'Active'
                      }
                      color={
                        moment(item?.expiryDate).isBefore()
                          ? 'danger'
                          : 'success'
                      }
                    />
                  </div>
                </div>

                <div className="border-t p-1 mt-1"></div>

                <div>
                  <ul className="space-y-1.5">
                    <li className="flex flex-row justify-between items-center">
                      <p className="text-body">Issue Date</p>
                      <p className="text-body-bold">
                        {moment(item?.issueDate).format('ll') || '-'}
                      </p>
                    </li>
                    {item?.expiryDate &&
                      moment(item.expiryDate).isValid() &&
                      moment(item.expiryDate).format('YYYY-MM-DD') !==
                        '2100-02-02' && (
                        <li className="flex flex-row justify-between items-center">
                          <p className="text-body">Expiry Date</p>
                          <p className="text-body-bold">
                            {moment(item.expiryDate).format('ll')}
                          </p>
                        </li>
                      )}

                    <li className="flex flex-row justify-between items-center">
                      <p className="text-body">Jurisdiction / Region</p>
                      <p className="text-body-bold">
                        {item?.jurisdiction || '-'}
                      </p>
                    </li>

                    {item?.licenseNo && (
                      <li className="flex flex-row justify-between items-center">
                        <p className="text-body">License / Certificate No</p>
                        <p className="text-body-bold">{item.licenseNo}</p>
                      </li>
                    )}

                    {item?.licenseLink && (
                      <li className="flex flex-row justify-between items-center">
                        <p className="text-body">License / Certificate Link</p>
                        <a
                          className="text-body-bold text-primary underline"
                          href={item.licenseLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Certificate
                        </a>
                      </li>
                    )}
                  </ul>
                </div>

                <div className="border-t py-1 mt-2"></div>

                <div className="space-y-4">
                  <p className="text-body line-clamp-4 overflow-hidden">
                    {item.description || '-'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Description Modal */}
      <Modal
        isOpen={descriptionModal.isOpen}
        onOpenChange={descriptionModal.onOpenChange}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalBody>
                <p className="text-body"></p>
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  Close
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <div className="flex justify-end items-end">
        <Pagination
          className="pl-5 mt-10"
          showControls
          size="sm"
          page={currentPage}
          total={totalPage}
          onChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default ProviderCertificate;
